import { TemplateErrorComponent } from "@/components/error";
import { PendingTemplateComponent } from "@/components/pending";
import { LanguageSwitcher } from "@/components/template/language-switcher";
import SyncTemplate from "@/components/template/sync-template";
import { trpc } from "@/lib/trpc";
import GrapesJSEditor from "@papier/grapesjs-editor";
import { Schema } from "@papier/grapesjs-editor/src/components/editor/schema-builder/schema-sidebar-tab";
import { zodSlug } from "@papier/shared";
import { projects, templates, type LocaleEnum } from "@papier/shared";
import { Badge } from "@papier/ui/components/ui/badge";
import { Button } from "@papier/ui/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@papier/ui/components/ui/dropdown-menu";
import { Separator } from "@papier/ui/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@papier/ui/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@papier/ui/components/ui/tooltip";
import { cn } from "@papier/ui/lib/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { format, formatDistanceToNow } from "date-fns";
import {
  ArrowLeft,
  ArrowLeftRight,
  Bell,
  Calendar,
  Clock,
  Copy,
  Edit,
  Eye,
  File,
  GitBranch,
  History,
  Languages,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Monitor,
  MoreHorizontal,
  Server,
  Smartphone,
  Tablet,
  Trash2,
  User,
} from "lucide-react";
import { useState } from "react";
import z from "zod";

const templateSearchSchema = z.object({
  env: zodSlug().catch("base"),
});

export const Route = createFileRoute("/_app/projects/$projectSlug/templates/$templateSlug/")({
  component: TemplateIndex,
  wrapInSuspense: true,
  params: {
    parse: (params) => {
      return z
        .object({
          projectSlug: projects.shape.slug,
          templateSlug: templates.shape.slug,
        })
        .parse(params);
    },
  },
  validateSearch: templateSearchSchema,
  pendingComponent: () => <PendingTemplateComponent />,
  errorComponent: ({ error }) => <TemplateErrorComponent error={error} />,
  loaderDeps: ({ search: { env } }) => ({ env }),
  loader: ({
    context: { queryClient, trpc },
    params: { projectSlug, templateSlug },
    deps: { env },
  }) => {
    const projectData = queryClient.getQueryData(trpc.projects.getBySlug.queryKey({ projectSlug }));
    if (projectData) {
      queryClient.ensureQueryData(
        trpc.templates.templates.getPublishedTemplate.queryOptions({
          projectId: projectData.id,
          templateSlug,
          environmentSlug: env,
        }),
      );
    }
  },
  head: () => ({
    meta: [{ title: "Template" }],
  }),
});

function TemplateIndex() {
  const { useParams, useSearch, useNavigate, Link } = Route;
  const { projectSlug, templateSlug } = useParams();
  const { env } = useSearch();
  const navigate = useNavigate();

  const { data: projectId } = useSuspenseQuery(
    trpc.projects.getBySlug.queryOptions({ projectSlug }, { select: (data) => data.id }),
  );
  const { data: template } = useSuspenseQuery(
    trpc.templates.templates.getPublishedTemplate.queryOptions({
      templateSlug,
      projectId: projectId,
      environmentSlug: env,
    }),
  );

  const currentEnvironment = template.environments.find((envItem) => envItem.slug === env);

  const [previewMode, setPreviewMode] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [currentLanguage, setCurrentLanguage] = useState<LocaleEnum>(
    template.locales[0] || "en-US",
  );

  const previewSizes = {
    desktop: "max-w-6xl",
    tablet: "max-w-2xl",
    mobile: "max-w-sm",
  };

  const content = template.localeContent.find((lc) => lc.locale === currentLanguage)?.content;
  if (!template || !content || !currentEnvironment) {
    return null;
  }

  return (
    <main className="grid gap-5">
      {(template.areEnvsOutOfSync || !template.isEveryEnvironmentLatest) && (
        <SyncTemplate
          templateId={template.id}
          environments={template.environments}
          versions={template.versions}
        />
      )}

      {/* Header */}
      <Card className="glass-card">
        <CardHeader className="gap-x-5 gap-y-0">
          <div className="flex items-start gap-2">
            <Button
              variant="ghost"
              title="Project"
              size="icon"
              onClick={() => navigate({ to: "/projects/$projectSlug" })}
            >
              <ArrowLeft className="size-5" />
            </Button>
            <div>
              <CardTitle className="text-lg">{template.name}</CardTitle>
              <CardDescription>{template.description}</CardDescription>
              <div className="flex items-center gap-2 pt-2">
                <Badge variant="outline" className="[&_svg]:size-4">
                  {template.type === "email" && (
                    <span className="flex items-center gap-1">
                      <Mail />
                      Email
                    </span>
                  )}
                  {template.type === "sms" && (
                    <span className="flex items-center gap-1">
                      <MessageSquare />
                      SMS
                    </span>
                  )}
                  {template.type === "document" && (
                    <span className="flex items-center gap-1">
                      <File />
                      Document
                    </span>
                  )}
                  {template.type === "push_notification" && (
                    <span className="flex items-center gap-1">
                      <Bell />
                      Push Notification
                    </span>
                  )}
                </Badge>
                <Badge variant="outline">
                  <span className="flex items-center gap-1">
                    <Server className="size-[13px]" />
                    {currentEnvironment?.name}
                  </span>
                </Badge>
                <Badge variant="outline">v{template.version}</Badge>
              </div>
            </div>
          </div>
          <CardAction>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button variant="outline" asChild>
                <Link
                  className="cursor-default"
                  to="/projects/$projectSlug/templates/$templateSlug/edit"
                >
                  <Edit />
                  <span className="hidden md:inline">Edit</span>
                </Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border-white/20">
                  <DropdownMenuItem>
                    <Copy />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive-foreground focus:text-destructive-foreground">
                    <Trash2 className="text-destructive-foreground" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardAction>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-8">
        {/* Template Preview */}
        <Card className="glass-card grid gap-5 lg:col-span-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Eye className="size-5" />
                Preview
              </CardTitle>
              <div className="flex items-center gap-3">
                <LanguageSwitcher
                  currentLocale={currentLanguage}
                  availableLocales={template.locales}
                  onChange={setCurrentLanguage}
                />
                <Separator orientation="vertical" className="h-5!" />
                <ToggleGroup
                  type="single"
                  className="hidden md:block"
                  variant="contrast"
                  value={previewMode}
                  onValueChange={(value) =>
                    setPreviewMode(value as "desktop" | "tablet" | "mobile")
                  }
                >
                  <ToggleGroupItem value="desktop" aria-label="Toggle Desktop">
                    <Monitor />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="tablet" aria-label="Toggle Tablet">
                    <Tablet />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="mobile" aria-label="Toggle Mobile">
                    <Smartphone />
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div
              className={cn(
                "border-border mx-auto h-svh w-full overflow-y-auto rounded-sm border",
                previewSizes[previewMode],
              )}
            >
              <div className="size-full bg-white">
                <GrapesJSEditor
                  initialContent={{
                    components: JSON.parse(content.html),
                    style: JSON.parse(content.css),
                  }}
                  mode="preview"
                  initialSchema={template.schemaDefinition as Schema}
                  onSchemaChange={() => {}}
                  onUpdate={() => {}}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Template Sidebar */}
        <div className="space-y-4 lg:col-span-2">
          {/* Template Info */}
          <Card className="glass-card gap-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutDashboard className="size-5" />
                Template Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 text-sm">
                <div className="flex items-center gap-1.5">
                  <Calendar className="text-muted-foreground size-4" />
                  <span className="text-muted-foreground">Created:</span>
                  <span className="">{format(template.createdAt, "MMM d, yyyy")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="text-muted-foreground size-4" />
                  <span className="text-muted-foreground">Updated:</span>
                  <span className="">
                    {formatDistanceToNow(template.updatedAt, { addSuffix: true })}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <GitBranch className="text-muted-foreground size-4" />
                  <span className="text-muted-foreground">Version:</span>
                  <span>v{template.version}</span>
                </div>
              </div>
            </CardContent>
            <Separator />
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="size-5" />
                Created By
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-medium">{template.createdBy?.name || "Unknown"}</div>
              <div className="text-muted-foreground text-xs">
                {template.createdBy?.email || "Unknown"}
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="size-5" />
                Environments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {template.environments.map((envItem) => (
                <div key={envItem.id} className="flex h-10 items-center justify-between py-1">
                  <div className="flex items-end gap-2">
                    <h4 className="text-foreground font-medium">{envItem.name}</h4>
                    <Badge variant="outline" className="w-13 cursor-default select-all">
                      v{envItem.version}
                    </Badge>
                  </div>
                  {env === envItem.slug ? (
                    <Tooltip>
                      <TooltipTrigger className="border-border grid size-8 place-items-center rounded-md border">
                        <ArrowLeftRight className="text-muted-foreground size-4" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Current</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant="outline"
                          className="size-8"
                          onClick={() => navigate({ search: { env: envItem.slug } })}
                        >
                          <ArrowLeftRight />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Switch to {envItem.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="size-5" />
                Versions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {template.versions.map((version) => (
                <div
                  key={`${version.slug}-${version.version}`}
                  className="flex items-center justify-between"
                >
                  <Button
                    asChild
                    variant="ghost"
                    className="flex w-full items-center justify-between gap-2"
                  >
                    <Link to="/grapes">
                      <span className="text-foreground text-sm">v{version.version}</span>
                      <span className="text-muted-foreground text-xs">
                        {format(version.createdAt, "MMM d, yyyy")}
                      </span>
                    </Link>
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Languages className="size-5" />
                Languages
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {template.locales.map((locale) => (
                <div key={locale} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground text-sm">{locale}</span>
                    {locale === "en-US" && (
                      <Badge
                        variant="outline"
                        className="text-muted-foreground border-white/30 text-xs"
                      >
                        Default
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}
